import type { SearchGroup, SearchHit } from "./searchService";

/** Pure state machine behind the Ctrl+K palette (Module 25): typing, results and keyboard travel. */
export type PaletteState = {
  query: string;
  groups: SearchGroup[];
  loading: boolean;
  /** Index into flattenHits(groups); -1 means "nothing highlighted" (Enter opens the results page). */
  highlighted: number;
  error: string | null;
};

export type PaletteAction =
  | { type: "reset"; query?: string }
  | { type: "query"; query: string }
  | { type: "loading" }
  | { type: "results"; query: string; groups: SearchGroup[] }
  | { type: "error"; message: string }
  | { type: "move"; delta: 1 | -1 }
  | { type: "highlight"; index: number };

export const initialPaletteState = (query = ""): PaletteState => ({
  query,
  groups: [],
  loading: false,
  highlighted: -1,
  error: null,
});

/** Hits in display order across the non-empty groups, so arrow keys travel the whole list. */
export const flattenHits = (groups: SearchGroup[]): SearchHit[] => groups.flatMap((g) => g.hits);

export const nonEmptyGroups = (groups: SearchGroup[]): SearchGroup[] =>
  groups.filter((g) => g.hits.length > 0);

export function paletteReducer(state: PaletteState, action: PaletteAction): PaletteState {
  switch (action.type) {
    case "reset":
      return initialPaletteState(action.query ?? "");
    case "query":
      return {
        ...state,
        query: action.query,
        highlighted: -1,
        error: null,
        ...(action.query.trim().length < 2 ? { groups: [], loading: false } : {}),
      };
    case "loading":
      return { ...state, loading: true, error: null };
    case "results":
      // A late answer for an older query must not replace the current one.
      if (action.query !== state.query.trim()) return state;
      return {
        ...state,
        groups: action.groups,
        loading: false,
        error: null,
        highlighted: flattenHits(action.groups).length ? 0 : -1,
      };
    case "error":
      return { ...state, loading: false, error: action.message };
    case "move": {
      const count = flattenHits(state.groups).length;
      if (!count) return state;
      // -1 (nothing) sits before the first hit; the list wraps at both ends.
      const next = state.highlighted + action.delta;
      if (next < -1) return { ...state, highlighted: count - 1 };
      if (next >= count) return { ...state, highlighted: -1 };
      return { ...state, highlighted: next };
    }
    case "highlight":
      return { ...state, highlighted: action.index };
    default:
      return state;
  }
}

/** What Enter should do: open the highlighted hit, or the results page for the query. */
export const enterTarget = (
  state: PaletteState
): { kind: "hit"; hit: SearchHit } | { kind: "results"; query: string } | null => {
  const hits = flattenHits(state.groups);
  if (state.highlighted >= 0 && hits[state.highlighted])
    return { kind: "hit", hit: hits[state.highlighted] };
  const query = state.query.trim();
  return query.length >= 2 ? { kind: "results", query } : null;
};
